import { logError } from '@zg-rentals/log-error';

import type { QueryClient } from '@zg-rentals/react-query5';
import type {
  AccountViewListingAdvertisingDto,
  AccountViewListingAdvertisingSpotlightDto,
  PlacementSource,
} from '@zg-rentals/ts-rental-manager-api-client';

/**
 * Pure function to build query predicate for advertising data
 */
export const buildAdvertisingQueryPredicate = (listingAlias: string) => ({
  predicate: (query: { queryKey: unknown }) => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      typeof key[0] === 'string' &&
      (key[0].includes('advertising') || key[0].includes('Advertising')) &&
      typeof key[1] === 'object' &&
      key[1] !== null &&
      (key[1] as { listingAlias?: string }).listingAlias === listingAlias
    );
  },
});

/**
 * Pure function to get current placement source from cache
 */
export const getCurrentPlacementSource = (
  queryClient: QueryClient,
  listingAlias: string,
): PlacementSource | null | undefined => {
  let result: PlacementSource | null | undefined = null;

  queryClient.getQueriesData(buildAdvertisingQueryPredicate(listingAlias)).forEach(([, data]) => {
    if (data && typeof data === 'object' && 'listingSpotlight' in data) {
      result = (data as AccountViewListingAdvertisingDto).listingSpotlight?.placementSource;
    }
  });

  return result;
};

/**
 * Pure function to update advertising cache optimistically
 */
export const updateAdvertisingCache = (
  queryClient: QueryClient,
  listingAlias: string,
  placementSource: PlacementSource | null,
): number => {
  const updated = queryClient.setQueriesData(
    buildAdvertisingQueryPredicate(listingAlias),
    (old: AccountViewListingAdvertisingDto | undefined) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        // Only placementSource is updated optimistically. isSpotlighted is owned by the backend (the
        // optimizer decides), so it's left untouched here and refreshed by the post-mutation advertising
        // refetch; consumers derive immediate pinned state from placementSource when needed.
        // The generated DTO marks placementSource non-nullable, but the API returns null for
        // eligible-but-not-selected rows and every consumer handles it — cast until the type is fixed.
        listingSpotlight: {
          ...old.listingSpotlight,
          placementSource,
        } as AccountViewListingAdvertisingSpotlightDto,
      };
    },
  );

  if (updated.length === 0) {
    logError({ error: new Error('No advertising queries found for listing'), context: { listingAlias } });
  }

  return updated.length;
};

/**
 * Pure helper to drop an item from a Set immutably
 */
export const dropFrom = <T>(set: Set<T>, item: T): Set<T> => {
  if (!set.has(item)) {
    return set;
  }
  const next = new Set(set);
  next.delete(item);
  return next;
};
